import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const RuleCard = ({ 
  rule, 
  onEdit, 
  onDelete, 
  onToggleStatus, 
  onTest,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-success';
    if (confidence >= 0.6) return 'text-warning';
    return 'text-error';
  };

  const formatPatterns = (patterns) => {
    if (!patterns || patterns?.length === 0) return 'No patterns';
    if (patterns?.length === 1) return patterns?.[0];
    return `${patterns?.[0]} +${patterns?.length - 1} more`;
  };

  return (
    <div className={`bg-card border border-border rounded-lg hover:shadow-moderate transition-smooth ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-foreground truncate">
                {rule?.name}
              </h3>
              <StatusIndicator
                status={rule?.isActive ? 'reviewed' : 'pending'}
                size="sm"
                showText={false}
                confidence={rule?.confidenceThreshold || 0}
              />
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Icon name="Target" size={14} />
                <span>{rule?.patterns?.length || 0} patterns</span>
              </span>
              <span className="flex items-center space-x-1">
                <Icon name="Users" size={14} />
                <span className={rule?._vendorIsPlaceholder ? 'text-muted-foreground italic' : ''}>
                  {rule?.vendorName}
                </span>
              </span>
              <span className="flex items-center space-x-1">
                <Icon name="TrendingUp" size={14} />
                <span className={getConfidenceColor(rule?.confidenceThreshold)}>
                  {Math.round(rule?.confidenceThreshold * 100)}% threshold
                </span>
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onTest(rule)}
              iconName="Play"
              iconPosition="left"
            >
              Test
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            />
          </div>
        </div>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Patterns */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2">Matching Patterns</h4>
            <div className="space-y-1">
              {rule?.patterns?.map((pattern, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  <Icon name="Search" size={12} className="text-muted-foreground" />
                  <code className="px-2 py-1 bg-muted rounded text-xs font-mono">
                    {pattern}
                  </code>
                  <span className="text-muted-foreground">
                    ({rule?.patternType === 'regex' ? 'Regex' : 'Fuzzy'})
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Statistics */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-muted/50 rounded-md">
              <div className="text-lg font-semibold text-foreground">
                {rule?.matchCount ?? <span className="text-muted-foreground italic">&lt;placeholder&gt;</span>}
              </div>
              <div className="text-xs text-muted-foreground">Total Matches</div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <div className="text-lg font-semibold text-success">
                {rule?.successRate == null ? (
                  <span className="text-muted-foreground italic">&lt;placeholder&gt;</span>
                ) : (
                  `${Math.round(rule?.successRate * 100)}%`
                )}
              </div>
              <div className="text-xs text-muted-foreground">Success Rate</div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-md">
              <div className="text-lg font-semibold text-foreground">
                {rule?._priorityIsPlaceholder ? (
                  <span className="text-muted-foreground italic">&lt;placeholder&gt;</span>
                ) : (
                  rule?.priority
                )}
              </div>
              <div className="text-xs text-muted-foreground">Priority</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <button
                onClick={() => onToggleStatus(rule?.id)}
                className={`
                  flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium transition-smooth
                  ${rule?.isActive 
                    ? 'bg-success/10 text-success hover:bg-success/20' 
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }
                `}
              >
                <div className={`w-2 h-2 rounded-full ${rule?.isActive ? 'bg-success' : 'bg-muted-foreground'}`} />
                <span>{rule?.isActive ? 'Active' : 'Inactive'}</span>
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(rule)}
                iconName="Edit"
                iconPosition="left"
              >
                Edit
              </Button>
              
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onDelete(rule?.id)}
                iconName="Trash2"
                iconPosition="left"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RuleCard;
